import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import COLORS from "../../config/colors";
import { cmsUrl } from "../../utils/CmsUtils";
import ImageOrVideo from "../ImageOrVideo";
import SimpleOverlay from "../modal/SimpleOverlay";
import RoomEditorOverlay from "../RoomEditor/overlay";
import classnames from "classnames";
import { useAnimationControls } from "motion/react";
import Carousel from "../Carousel";
import "./style.scss";

const Gallery = ({
  artId = "",
  images = [],
  containImage = false,
  has3DView = false,
  startIndex = 0,
  children
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(
    images.length > startIndex ? startIndex : 0
  );
  const prevSelectedIndex = useRef(selectedIndex);
  const sliderRef = useRef();
  const [is3DOpen, setIs3DOpen] = useState(false);

  const items = images?.map(image => {
    return {
      image: true,
      path:
        image.styles?.full ||
        image.styles?.px3840 ||
        cmsUrl("/storage/uploads" + image.path),
      colors: image.colors
    };
  });
  const previewItems = images?.map(image => {
    return {
      image: true,
      path:
        image.styles?.medium ||
        image.styles?.px1920 ||
        cmsUrl("/storage/uploads" + image.path),
      colors: image.colors
    };
  });
  const thumbItems = images?.map(image => {
    return {
      image: true,
      path:
        image.styles?.thumbnail ||
        image.styles?.px128 ||
        cmsUrl("/storage/uploads" + image.path),
      colors: image.colors
    };
  });

  //console.log("Gallery", selectedIndex);

  const switchPreviewControl = useAnimationControls();
  useEffect(() => {
    if (prevSelectedIndex.current !== selectedIndex) {
      switchPreviewControl.start({
        opacity: [0.5, 1],
        scale: [0.9, 1],
        transition: {
          type: "spring",
          stiffness: 500,
          damping: 30
        }
      });
      prevSelectedIndex.current = selectedIndex;
    }
  }, [selectedIndex, switchPreviewControl]);

  if (!images) return null;

  return (
    <div className={"gallery"}>
      <AnimatePresence>
        {modalIsOpen && (
          <SimpleOverlay animateScale close={() => setModalIsOpen(false)}>
            <Carousel
              ref={sliderRef}
              className="gallery-carousel"
              direction="horizontal"
              showMarkers
              smallMarkers
              centeredButtons
              slidesData={items}
              renderSlide={(image, idx) => (
                <div className={"carousel-slide"} key={idx}>
                  <ImageOrVideo
                    key={idx}
                    image={image}
                    containImage
                    centerImage
                    className="slide-content"
                  />
                </div>
              )}
              loop
              startIndex={selectedIndex}
              onSlideChange={index => setSelectedIndex(index)}
            />
          </SimpleOverlay>
        )}
      </AnimatePresence>
      {has3DView && artId && (
        <RoomEditorOverlay
          isOpen={is3DOpen}
          close={() => setIs3DOpen(false)}
          initialFloor={artId}
        />
      )}
      <motion.div
        className="preview"
        onClick={() => setModalIsOpen(true)}
        animate={switchPreviewControl}
      >
        <ImageOrVideo
          image={previewItems[selectedIndex]}
          containImage={containImage}
        />
        {children}
      </motion.div>

      {thumbItems.length > 1 && (
        <div className={"thumbnails"}>
          {thumbItems.map((image, idx) => {
            return (
              <div
                className={classnames(
                  "thumbnail",
                  idx === selectedIndex && "selected"
                )}
                onClick={() => setSelectedIndex(idx)}
                key={idx}
              >
                <ImageOrVideo image={image} />
              </div>
            );
          })}
          {has3DView && artId && (
            <div className={"thumbnail"} key={"3d"}>
              <motion.div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: COLORS.bjelin_dark_grey,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                onClick={() => setIs3DOpen(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 52.42 60.4"
                  style={{ width: "80%" }}
                >
                  <g fill="white">
                    <polygon
                      opacity={0.17}
                      points="26.83 29.54 26.83 0 52.42 14.77 52.42 44.31 26.83 29.54"
                    />
                    <polygon
                      opacity={0.17}
                      points="25.58 29.54 25.58 0 0 14.77 0 44.31 25.58 29.54"
                    />
                    <polygon
                      opacity={0.17}
                      points="26.24 30.86 .65 45.63 26.24 60.4 51.82 45.63 26.24 30.86"
                    />
                    <g>
                      <path d="M24.63,24.81c0,1.07-.32,1.98-.97,2.73s-1.56,1.27-2.73,1.55v.07c1.38,.17,2.43,.59,3.14,1.26s1.07,1.57,1.07,2.7c0,1.65-.6,2.93-1.79,3.85s-2.9,1.38-5.12,1.38c-1.86,0-3.51-.31-4.95-.93v-3.08c.66,.34,1.39,.61,2.19,.82s1.59,.32,2.37,.32c1.2,0,2.08-.2,2.65-.61s.86-1.06,.86-1.96c0-.8-.33-1.38-.98-1.71s-1.7-.5-3.14-.5h-1.3v-2.78h1.32c1.33,0,2.3-.17,2.91-.52s.92-.94,.92-1.79c0-1.3-.81-1.95-2.44-1.95-.56,0-1.13,.09-1.72,.28s-1.23,.51-1.94,.97l-1.68-2.5c1.56-1.12,3.43-1.69,5.59-1.69,1.77,0,3.17,.36,4.2,1.08s1.54,1.72,1.54,3Z" />
                      <path d="M41.9,29.38c0,2.82-.8,4.98-2.41,6.48s-3.92,2.25-6.96,2.25h-4.85V20.98h5.38c2.8,0,4.97,.74,6.52,2.21s2.32,3.54,2.32,6.19Zm-3.77,.09c0-3.68-1.62-5.52-4.88-5.52h-1.93v11.16h1.56c3.5,0,5.25-1.88,5.25-5.64Z" />
                    </g>
                    <g>
                      <path d="M17.32,9.92h1.22l-1.94,5.71h-1.32l-1.94-5.71h1.22l1.07,3.4c.06,.2,.12,.43,.19,.7s.1,.45,.12,.56c.03-.24,.13-.66,.29-1.26l1.08-3.4Z" />
                      <path d="M19.17,10.13c0-.39,.22-.58,.65-.58s.65,.19,.65,.58c0,.19-.05,.33-.16,.43s-.27,.15-.49,.15c-.43,0-.65-.2-.65-.59Zm1.24,5.5h-1.19v-4.37h1.19v4.37Z" />
                      <path d="M23.99,11.18c.16,0,.3,.01,.4,.04l-.09,1.12c-.1-.03-.21-.04-.35-.04-.38,0-.68,.1-.89,.29s-.32,.47-.32,.82v2.22h-1.19v-4.37h.9l.18,.73h.06c.14-.24,.32-.44,.55-.59s.48-.22,.75-.22Z" />
                      <path d="M27.13,14.76c.21,0,.46-.05,.75-.14v.89c-.3,.13-.66,.2-1.09,.2-.48,0-.82-.12-1.04-.36s-.33-.6-.33-1.08v-2.11h-.57v-.5l.66-.4,.34-.92h.76v.93h1.22v.89h-1.22v2.11c0,.17,.05,.29,.14,.38s.22,.12,.38,.12Z" />
                      <path d="M31.73,15.63l-.16-.56h-.06c-.13,.2-.31,.36-.54,.47s-.5,.17-.8,.17c-.51,0-.9-.14-1.16-.41s-.39-.67-.39-1.19v-2.85h1.19v2.55c0,.31,.06,.55,.17,.71s.29,.24,.54,.24c.33,0,.57-.11,.72-.33s.22-.59,.22-1.11v-2.05h1.19v4.37h-.91Z" />
                      <path d="M36.56,15.63l-.23-.59h-.03c-.2,.25-.41,.43-.62,.53s-.49,.15-.83,.15c-.42,0-.75-.12-.99-.36s-.36-.58-.36-1.02,.16-.81,.49-1.03,.81-.34,1.47-.37l.76-.02v-.19c0-.44-.23-.66-.68-.66-.35,0-.76,.11-1.23,.32l-.39-.8c.5-.26,1.06-.39,1.67-.39s1.04,.13,1.35,.38,.47,.64,.47,1.16v2.91h-.83Zm-.35-2.02l-.46,.02c-.35,.01-.6,.07-.77,.19s-.25,.29-.25,.52c0,.34,.19,.5,.58,.5,.28,0,.5-.08,.66-.24s.25-.37,.25-.63v-.36Z" />
                      <path d="M39.7,15.63h-1.19v-6.08h1.19v6.08Z" />
                    </g>
                    <g>
                      <path d="M9.77,46.56c0,.52-.19,.92-.56,1.22s-.89,.45-1.55,.45c-.61,0-1.15-.11-1.62-.34v-1.12c.39,.17,.71,.29,.98,.36s.51,.11,.73,.11c.27,0,.47-.05,.61-.15s.21-.25,.21-.45c0-.11-.03-.21-.09-.3s-.15-.17-.28-.25-.37-.21-.74-.39c-.35-.16-.61-.32-.79-.47s-.31-.33-.42-.53-.16-.44-.16-.7c0-.51,.17-.9,.51-1.19s.82-.43,1.42-.43c.3,0,.58,.04,.85,.11s.55,.17,.85,.3l-.39,.94c-.3-.12-.56-.21-.76-.26s-.4-.07-.59-.07c-.23,0-.4,.05-.53,.16s-.18,.25-.18,.42c0,.11,.02,.2,.07,.28s.13,.16,.24,.23,.36,.21,.77,.4c.53,.26,.9,.51,1.1,.77s.3,.57,.3,.94Z" />
                      <path d="M14.69,48.15h-1.19v-2.55c0-.63-.23-.95-.7-.95-.33,0-.57,.11-.72,.34s-.22,.59-.22,1.1v2.05h-1.19v-6.08h1.19v1.24c0,.1,0,.32-.03,.68l-.03,.35h.06c.27-.43,.69-.64,1.27-.64,.51,0,.9,.14,1.17,.41s.4,.67,.4,1.19v2.85Z" />
                      <path d="M19.81,45.96c0,.71-.19,1.27-.56,1.67s-.9,.6-1.57,.6c-.42,0-.79-.09-1.11-.28s-.57-.45-.74-.79-.26-.74-.26-1.2c0-.71,.19-1.27,.56-1.66s.9-.59,1.57-.59c.42,0,.79,.09,1.11,.27s.57,.44,.74,.79,.26,.74,.26,1.2Zm-3.02,0c0,.43,.07,.76,.21,.98s.37,.33,.69,.33,.55-.11,.69-.33,.21-.55,.21-.98-.07-.76-.21-.97-.37-.32-.69-.32-.55,.11-.69,.32-.21,.54-.21,.97Z" />
                      <path d="M24.24,48.15l-.34-1.53-.45-1.93h-.03l-.8,3.46h-1.28l-1.24-4.37h1.19l.5,1.93c.08,.35,.16,.82,.25,1.43h.02c.01-.2,.06-.51,.14-.94l.06-.33,.54-2.09h1.31l.51,2.09c.01,.06,.03,.14,.05,.25s.04,.23,.06,.36,.04,.25,.06,.37,.03,.22,.03,.29h.02c.02-.19,.06-.44,.12-.77s.1-.55,.13-.66l.52-1.93h1.17l-1.26,4.37h-1.3Z" />
                      <path d="M29.88,43.7c.16,0,.3,.01,.4,.04l-.09,1.12c-.1-.03-.21-.04-.35-.04-.38,0-.68,.1-.89,.29s-.32,.47-.32,.82v2.22h-1.19v-4.37h.9l.18,.73h.06c.14-.24,.32-.44,.55-.59s.48-.22,.75-.22Z" />
                      <path d="M34.96,45.96c0,.71-.19,1.27-.56,1.67s-.9,.6-1.57,.6c-.42,0-.79-.09-1.11-.28s-.57-.45-.74-.79-.26-.74-.26-1.2c0-.71,.19-1.27,.56-1.66s.9-.59,1.57-.59c.42,0,.79,.09,1.11,.27s.57,.44,.74,.79,.26,.74,.26,1.2Zm-3.02,0c0,.43,.07,.76,.21,.98s.37,.33,.69,.33,.55-.11,.69-.33,.21-.55,.21-.98-.07-.76-.21-.97-.37-.32-.69-.32-.55,.11-.69,.32-.21,.54-.21,.97Z" />
                      <path d="M39.86,45.96c0,.71-.19,1.27-.56,1.67s-.9,.6-1.57,.6c-.42,0-.79-.09-1.11-.28s-.57-.45-.74-.79-.26-.74-.26-1.2c0-.71,.19-1.27,.56-1.66s.9-.59,1.57-.59c.42,0,.79,.09,1.11,.27s.57,.44,.74,.79,.26,.74,.26,1.2Zm-3.02,0c0,.43,.07,.76,.21,.98s.37,.33,.69,.33,.55-.11,.69-.33,.21-.55,.21-.98-.07-.76-.21-.97-.37-.32-.69-.32-.55,.11-.69,.32-.21,.54-.21,.97Z" />
                      <path d="M44.67,48.15h-1.19v-2.55c0-.31-.05-.55-.16-.71s-.27-.24-.5-.24c-.3,0-.53,.11-.66,.34s-.21,.59-.21,1.11v2.05h-1.19v-4.37h.91l.16,.56h.07c.12-.2,.29-.36,.51-.47s.48-.17,.76-.17c.65,0,1.1,.21,1.33,.64h.11c.12-.2,.29-.36,.52-.47s.48-.17,.77-.17c.49,0,.87,.13,1.12,.38s.38,.66,.38,1.22v2.85h-1.2v-2.55c0-.31-.05-.55-.16-.71s-.27-.24-.5-.24c-.29,0-.51,.1-.65,.31s-.22,.54-.22,.99v2.19Z" />
                    </g>
                  </g>
                </svg>
              </motion.div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Gallery;
